import React, { useState, useEffect, createContext, useContext } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from './lib/queryClient';
import FlowEditor from './pages/Editor/index.tsx';
import Home from './pages/Home/index.tsx';
import { WorkspaceShell, EditorLayout, SettingsLayout, CommandMenuProvider } from './components/shell';
import TemplatesPage from './pages/Templates/index.tsx';
import MyNodesPage from './pages/MyNodes/index.tsx';
import PricingPlans from './pages/Billing/index.tsx';
import BillingManagement from './pages/Billing/BillingManagement.tsx';
import CheckoutSuccess from './pages/Billing/CheckoutSuccess.tsx';
import PublicDiagramViewer from './pages/Sharing/PublicDiagramViewer.tsx';
import EmbedDiagramViewer from './pages/Sharing/EmbedDiagramViewer.tsx';
import SettingsPage from './pages/Settings/index.tsx';
import NotFound from './pages/NotFound/index.tsx';
import { AuthProvider, AuthPage, VerifyEmailPage } from './pages/Auth';
import { ForgotPasswordPage } from './pages/Auth/ForgotPasswordPage';
import { ResetPasswordPage } from './pages/Auth/ResetPasswordPage';
import { PostHogProvider } from './lib/posthog';
import { ProtectedRoute } from './components/routing';
import { OnboardingProvider } from './context/OnboardingContext';
import { TabsProvider } from './context/TabsContext';
import { SaveStatusProvider } from './context/SaveStatusContext';
import { SubscriptionSyncProvider } from './context/SubscriptionSyncContext';
import { ToastProvider } from './context/ToastContext';
import { ConfirmProvider } from './context/ConfirmContext';

// Theme context for sharing theme state across routes
interface ThemeContextType {
  isDark: boolean;
  toggleTheme: () => void;
  setTheme: (isDark: boolean) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};

function ThemeProvider({ children }: { children: React.ReactNode }) {
  // Initialize from local storage or default to dark
  const [isDark, setIsDark] = useState(() => {
    const saved = localStorage.getItem('theme');
    return saved ? saved === 'dark' : true;
  });

  useEffect(() => {
    const root = window.document.documentElement;
    if (isDark) {
      root.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      root.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  }, [isDark]);

  const toggleTheme = () => setIsDark(!isDark);
  const setTheme = (value: boolean) => setIsDark(value);

  return (
    <ThemeContext.Provider value={{ isDark, toggleTheme, setTheme }}>
      <div
        className="w-full min-h-screen transition-colors duration-200 bg-background text-foreground selection:bg-[#0d41ff]/30"
      >
        {children}
      </div>
    </ThemeContext.Provider>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <PostHogProvider>
          <AuthProvider>
            <SubscriptionSyncProvider>
              <OnboardingProvider>
                <ThemeProvider>
                <ToastProvider>
                <ConfirmProvider>
                <TabsProvider>
                <SaveStatusProvider>
                <CommandMenuProvider>
              <Routes>
              {/* Public routes */}
              <Route path="/auth" element={<AuthPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/verify-email" element={<VerifyEmailPage />} />
              <Route path="/share/:shareToken" element={<PublicDiagramViewer />} />
              <Route path="/embed/:shareToken" element={<EmbedDiagramViewer />} />
              <Route path="/demo" element={<DemoEditorWrapper />} />
              
              {/* Protected routes */}
              <Route element={<ProtectedRoute><WorkspaceShell /></ProtectedRoute>}>
                <Route path="/" element={<Home />} />
                <Route path="/templates" element={<TemplatesPage />} />
                <Route path="/nodes" element={<MyNodesPage />} />
                <Route path="/billing" element={<BillingManagement />} />
                <Route path="/pricing" element={<PricingPlans />} />
              </Route>
              <Route element={<ProtectedRoute><SettingsLayout /></ProtectedRoute>}>
                <Route path="/settings" element={<SettingsPage />} />
              </Route>
              <Route element={<ProtectedRoute><EditorLayout /></ProtectedRoute>}>
                <Route path="/editor" element={<FlowEditorWrapper />} />
                <Route path="/editor/:diagramId" element={<FlowEditorWrapper />} />
              </Route>
<Route path="/success" element={<ProtectedRoute><CheckoutSuccess /></ProtectedRoute>} />
              
              {/* 404 catch-all route */}
              <Route path="*" element={<NotFound />} />
              </Routes>
                </CommandMenuProvider>
                </SaveStatusProvider>
                </TabsProvider>
                </ConfirmProvider>
                </ToastProvider>
            </ThemeProvider>
          </OnboardingProvider>
            </SubscriptionSyncProvider>
      </AuthProvider>
    </PostHogProvider>
  </BrowserRouter>
</QueryClientProvider>
);
}

// Wrapper to pass theme props to FlowEditor
function FlowEditorWrapper() {
  const { isDark } = useTheme();
  return <FlowEditor isDark={isDark} />;
}

function DemoEditorWrapper() {
  const { isDark } = useTheme();
  // Standalone (no EditorLayout), so give the editor a full-height container.
  return (
    <div className="h-screen w-full">
      <FlowEditor demo isDark={isDark} />
    </div>
  );
}

export default App;
